import { useLazyQuery, useMutation } from '@vue/apollo-composable';
import { useToast } from 'primevue/usetoast';
import {
  DOWNLOAD_BULK_SCORE_SHEET,
  DOWNLOAD_BULK_CBT_SCORES,
  DOWNLOAD_BULK_SCORE_SHEET_WITH_CBT,
  PREVIEW_BULK_SCORE_SHEET,
  PREVIEW_BULK_CBT_SCORES,
  IMPORT_BULK_SCORE_SHEET,
} from '@/graphql/Queries/files';
import { getErrorMessage } from '@/utils/error';
import { loadOrRefetch } from '@/utils/lazyQuery';

/** Which export flavours a bulk-export panel should expose. */
export type BulkExportMode = 'score-sheets' | 'cbt-scores' | 'merged';

/** One CBT test row inside a preview course group. */
export interface BulkCbtTest {
  test_title: string;
  session_count: number;
}

export interface ScoreSheetPreviewItem {
  course_code?: string | null;
  course_title?: string | null;
  student_count: number;
}

export interface ScoreSheetPreview {
  items: ScoreSheetPreviewItem[];
  total_courses: number;
  has_template: boolean;
}

export interface CbtPreviewItem extends ScoreSheetPreviewItem {
  tests: BulkCbtTest[];
}

export interface CbtPreview {
  items: CbtPreviewItem[];
  unlinked_count: number;
  total_tests: number;
}

export interface MergedPreviewItem extends ScoreSheetPreviewItem {
  tests: BulkCbtTest[];
}

/**
 * Fold the CBT preview's per-course tests into the score-sheet preview rows,
 * keyed by course code (falling back to title). Extracted as a pure function so
 * the merge mapping can be unit-tested without mounting the panel.
 */
export function mergeCbtIntoScorePreview(
  scoreSheetPreview: ScoreSheetPreview | null,
  cbtPreview: CbtPreview | null,
): MergedPreviewItem[] {
  if (!scoreSheetPreview) return [];
  const cbtItems = cbtPreview?.items ?? [];
  const cbtMap = new Map<string, BulkCbtTest[]>();
  for (const item of cbtItems) {
    const key = item.course_code || item.course_title || '';
    cbtMap.set(key, item.tests ?? []);
  }
  return scoreSheetPreview.items.map((item) => ({
    ...item,
    tests: cbtMap.get(item.course_code || item.course_title || '') ?? [],
  }));
}

/**
 * Shared state + GraphQL plumbing behind every bulk-export surface (Results'
 * Score Entry "Bulk" toggle and the CBT section both render the same panel on
 * top of this). Each caller gets its own isolated filter/preview state.
 */
export function useBulkExport() {
  const toast = useToast();

  const selectedCalendarId = ref<string | null>(null);
  // undefined (not null) so the value matches ProgrammeSelect's modelValue type.
  const selectedProgrammeId = ref<string | undefined>(undefined);
  const selectedLevel = ref<string | null>(null);
  const selectedClassroomId = ref<string | null>(null);

  const scoreSheetPreview = ref<ScoreSheetPreview | null>(null);
  const cbtPreview = ref<CbtPreview | null>(null);
  const mergedMode = ref(false);

  function filterVars() {
    return {
      academic_calendar_id: selectedCalendarId.value,
      programme_id: selectedProgrammeId.value || undefined,
      level: selectedLevel.value || undefined,
      classroom_id: selectedClassroomId.value || undefined,
    };
  }

  function clearPreviews() {
    scoreSheetPreview.value = null;
    cbtPreview.value = null;
    mergedMode.value = false;
  }

  // --- Preview queries ---
  // Read the data each call returns (loadOrRefetch) rather than onResult: a
  // repeat preview with unchanged data emits no new result, which used to
  // leave the panel blank after it had been cleared for the reload.
  const previewScoreQuery = useLazyQuery(PREVIEW_BULK_SCORE_SHEET, undefined, { fetchPolicy: 'network-only' });
  const previewCbtQuery = useLazyQuery(PREVIEW_BULK_CBT_SCORES, undefined, { fetchPolicy: 'network-only' });
  const previewingScoreSheet = previewScoreQuery.loading;
  const previewingCbt = previewCbtQuery.loading;

  function previewFailed(error: unknown) {
    toast.add({ severity: 'error', summary: 'Preview failed', detail: getErrorMessage(error), life: 6000 });
  }

  async function loadScoreSheetPreview() {
    const data = await loadOrRefetch(previewScoreQuery, filterVars());
    scoreSheetPreview.value = data?.previewBulkScoreSheet ?? null;
  }

  async function loadCbtPreview() {
    const data = await loadOrRefetch(previewCbtQuery, filterVars());
    cbtPreview.value = data?.previewBulkCbtScores ?? null;
  }

  async function previewScoreSheet() {
    mergedMode.value = false;
    scoreSheetPreview.value = null;
    await loadScoreSheetPreview().catch(previewFailed);
  }

  async function previewCbtScores() {
    mergedMode.value = false;
    cbtPreview.value = null;
    await loadCbtPreview().catch(previewFailed);
  }

  async function previewMerged() {
    mergedMode.value = true;
    scoreSheetPreview.value = null;
    cbtPreview.value = null;
    await Promise.all([loadScoreSheetPreview(), loadCbtPreview()]).catch(previewFailed);
  }

  const mergedPreviewItems = computed(() => mergeCbtIntoScorePreview(scoreSheetPreview.value, cbtPreview.value));

  // --- Download queries (async — backend queues a job and the file lands in
  // the Downloads tray; we just surface a "started" toast). ---
  const scoreSheetExport = useLazyQuery(DOWNLOAD_BULK_SCORE_SHEET, undefined, { fetchPolicy: 'network-only' });
  const cbtExport = useLazyQuery(DOWNLOAD_BULK_CBT_SCORES, undefined, { fetchPolicy: 'network-only' });
  const mergedExport = useLazyQuery(DOWNLOAD_BULK_SCORE_SHEET_WITH_CBT, undefined, { fetchPolicy: 'network-only' });
  const loadingScoreSheet = scoreSheetExport.loading;
  const loadingCbt = cbtExport.loading;
  const loadingMerged = mergedExport.loading;

  /** Start an export; every click queues a new one (loadOrRefetch), and a refusal is shown. */
  async function startExport(query: typeof scoreSheetExport, field: string, detail: string) {
    try {
      const data = await loadOrRefetch(query, filterVars());
      if (data?.[field]) toast.add({ severity: 'info', summary: 'Export Started', detail, life: 5000 });
    } catch (error) {
      toast.add({ severity: 'error', summary: 'Export failed', detail: getErrorMessage(error), life: 6000 });
    }
  }

  function exportBulkScoreSheet() {
    return startExport(scoreSheetExport, 'downloadBulkScoreSheet',
      'Bulk score sheets are being generated. Check the Downloads icon in the top bar.');
  }

  function exportBulkCbtScores() {
    return startExport(cbtExport, 'downloadBulkCbtScores',
      'Bulk CBT scores are being generated. Check the Downloads icon in the top bar.');
  }

  function exportMerged() {
    return startExport(mergedExport, 'downloadBulkScoreSheetWithCbt',
      'Score sheets with CBT scores are being generated. Check the Downloads icon in the top bar.');
  }

  // --- Bulk import (upload multi-sheet Excel back) ---
  const { mutate: importBulkMutate, loading: importingBulk } = useMutation(IMPORT_BULK_SCORE_SHEET);

  async function importBulkScoreSheet(file: File) {
    if (!selectedCalendarId.value || !selectedClassroomId.value) {
      toast.add({ severity: 'warn', summary: 'Missing Filters', detail: 'Select an academic calendar and classroom before uploading.', life: 4000 });
      return;
    }
    try {
      const result = await importBulkMutate({
        file,
        classroom_id: selectedClassroomId.value,
        academic_calendar_id: selectedCalendarId.value,
      });
      const msg = result?.data?.importBulkScoreSheet;
      if (msg?.success) {
        toast.add({ severity: 'success', summary: 'Import Complete', detail: msg.message, life: 5000 });
      } else {
        toast.add({ severity: 'warn', summary: 'Import', detail: msg?.message ?? 'No sheets imported.', life: 5000 });
      }
    } catch (e) {
      toast.add({ severity: 'error', summary: 'Import Failed', detail: getErrorMessage(e, 'Import failed'), life: 5000 });
    }
  }

  return {
    // filters
    selectedCalendarId,
    selectedProgrammeId,
    selectedLevel,
    selectedClassroomId,
    // preview state
    scoreSheetPreview,
    cbtPreview,
    mergedMode,
    mergedPreviewItems,
    previewingScoreSheet,
    previewingCbt,
    // download state
    loadingScoreSheet,
    loadingCbt,
    loadingMerged,
    // actions
    clearPreviews,
    previewScoreSheet,
    previewCbtScores,
    previewMerged,
    exportBulkScoreSheet,
    exportBulkCbtScores,
    exportMerged,
    // bulk import
    importBulkScoreSheet,
    importingBulk,
  };
}
